'use client';

import { useMemo } from 'react';
import { MenuItem } from '@headlessui/react';
import classNames from 'classnames';
import { Variant } from 'shared/types/product';
import { Check } from 'components/gruenland/icons/Check';
import { ColorGroupCodeDot } from 'components/gruenland/molecules/colors/ColorGroupCodeDot';
import { useFormat } from 'helpers/hooks/useFormat';
import { discardRepeatedValues } from './helpers/discardRepeatedValues';
import { filterAttributeBasedVariants } from './helpers/filterAttributeBasedVariants';
import Picker from './Picker';

type Props = {
  currentVariant: Variant;
  variants: Variant[];
  onClick?: (sku: string) => void;
};

const ATTRIBUTE = 'colour';
const ATTRIBUTE_TO_FILTER_TO = 'size';

const ColorPicker: React.FC<Props> = ({ currentVariant, variants, onClick }) => {
  const { formatMessage } = useFormat({ name: 'gruenland' });
  const variantsToUse = useMemo(() => {
    const currentValue = currentVariant.attributes?.[ATTRIBUTE];
    const result = currentValue
      ? filterAttributeBasedVariants(variants, currentVariant, ATTRIBUTE_TO_FILTER_TO)
      : discardRepeatedValues(variants, ATTRIBUTE);
    const filteredVariants: Variant[] = [...(result || [])];
    return [...new Set(variants.map((x) => x.attributes?.[ATTRIBUTE]).filter(Boolean))].sort().map((attribute) => {
      const target = filteredVariants.find((x) => x.attributes?.[ATTRIBUTE] === attribute);
      return {
        available: !!target,
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        variant: target || variants.find((x) => x.attributes?.[ATTRIBUTE] === attribute)!,
      };
    });
  }, [currentVariant, variants]);

  return (
    <Picker
      label={
        <>
          <ColorGroupCodeDot
            as="div"
            colourGroupCode={currentVariant.attributes?.colourGroupCode}
            disableHover={true}
            className="group-data-[open]:hidden"
          />
          <span className="grow text-start font-semibold">
            <span className="capitalize group-data-[open]:hidden">
              {currentVariant.attributes?.colour?.toLowerCase?.()}
            </span>
            <span className="hidden group-data-[open]:inline">
              {formatMessage({ id: 'pdp.pickColor', defaultMessage: 'Seleziona colore' })}
            </span>
          </span>
        </>
      }
    >
      {variantsToUse.map(({ available, variant: { attributes, id, sku } }) => (
        <MenuItem key={id}>
          <div
            className={classNames(
              'flex cursor-pointer items-center gap-[30px] border-t border-neutrals-black-20 p-3 font-semibold hover:bg-neutral-50 lg:first:border-t-0',
              {
                'bg-neutral-100 text-neutral-500': !available,
              },
            )}
            onClick={() => onClick?.(sku)}
          >
            <ColorGroupCodeDot as="div" colourGroupCode={attributes?.colourGroupCode} disableHover={true} />
            <span className="grow capitalize">{attributes?.colour?.toLowerCase?.()}</span>
            {id === currentVariant.id && <Check />}
          </div>
        </MenuItem>
      ))}
    </Picker>
  );
};

export default ColorPicker;
